import type { SsoUserSuggestion } from '../types/api';
import { setApiAccessToken } from './http';
import axios from 'axios';

let ssoAccessToken: string | null = null;

export function setSsoAccessToken(token: string | null): void {
  ssoAccessToken = token;
  // keep API token in sync for the main backend client as well
  setApiAccessToken(token);
}

export async function searchSsoUsers(params: { q: string; limit?: number; page?: number }) {
  const ssoOrigin = process.env.REACT_APP_SSO_ORIGIN ?? 'https://sso.chatbot.mn';
  const response = await axios.get(`${ssoOrigin}/api/user`, {
    headers: ssoAccessToken
      ? {
          Authorization: `Bearer ${ssoAccessToken}`,
        }
      : undefined,
    params: {
      q: params.q,
      page: params.page ?? 1,
      limit: params.limit ?? 8,
    },
  });
  return response.data as { list?: SsoUserSuggestion[] };
}

/**
 * Mint an admin impersonation token. Calls the IdP, returns the new
 * access token + expiry + audit row id. Caller is responsible for storing
 * the token (see `auth/impersonation.ts`) and reloading the UI so the
 * operator's context takes effect.
 *
 * `reason` must be ≥8 chars — the IdP DTO rejects shorter ones. Surface
 * the validation message to the user verbatim.
 */
export async function startImpersonation(opts: {
  userId: string;
  reason: string;
  clientId?: string;
  ttlSec?: number;
}): Promise<{ accessToken: string; expiresAt: string; auditId: string }> {
  const ssoOrigin = process.env.REACT_APP_SSO_ORIGIN ?? 'https://sso.chatbot.mn';
  const response = await axios.post(
    `${ssoOrigin}/api/admin/impersonate`,
    {
      userId: opts.userId,
      reason: opts.reason,
      clientId: opts.clientId ?? 'booking-admin',
      ...(opts.ttlSec ? { ttlSec: opts.ttlSec } : {}),
    },
    {
      headers: ssoAccessToken ? { Authorization: `Bearer ${ssoAccessToken}` } : undefined,
    },
  );
  return response.data as { accessToken: string; expiresAt: string; auditId: string };
}

/**
 * End the impersonation session on the IdP side (marks audit `stoppedAt`).
 * Caller still needs to clear the local token + reload. Pass the
 * impersonation token explicitly because the singleton `ssoAccessToken`
 * stores the admin's normal token.
 */
export async function stopImpersonation(impersonationToken: string): Promise<void> {
  const ssoOrigin = process.env.REACT_APP_SSO_ORIGIN ?? 'https://sso.chatbot.mn';
  await axios.post(`${ssoOrigin}/api/admin/impersonate/stop`, undefined, {
    headers: { Authorization: `Bearer ${impersonationToken}` },
  });
}

/**
 * Fetch a single SSO user record. Returns `null` on 404 or any error
 * (e.g. the user no longer exists in SSO) so callers can render a
 * graceful fallback row instead of breaking the whole list.
 */
export async function getSsoUser(id: string): Promise<SsoUserSuggestion | null> {
  if (!id?.trim()) return null;
  const ssoOrigin = process.env.REACT_APP_SSO_ORIGIN ?? 'https://sso.chatbot.mn';
  try {
    const response = await axios.get(`${ssoOrigin}/api/user/${encodeURIComponent(id)}`, {
      headers: ssoAccessToken ? { Authorization: `Bearer ${ssoAccessToken}` } : undefined,
    });
    return response.data as SsoUserSuggestion;
  } catch {
    return null;
  }
}
